import { supabase } from '@/lib/supabaseClient';

export type ChefCoinAction =
  | 'STUDIO_GENERATE'
  | 'IMAGE_GENERATE'
  | 'RECIPE_MODIFY'
  | 'RECIPE_CREATE_FROM_TEXT'
  | 'RECIPE_CLEANUP'
  | 'RECIPE_QA'
  | 'NUTRITIONAL_ANALYSIS'
  | 'GENERAL_ADVICE';

export type ChefCoinTransactionType =
  | 'spend'
  | 'earn'
  | 'admin_adjustment'
  | 'monthly_reset'
  | 'refund';

export interface ChefCoinTransaction {
  id: string;
  user_id: string;
  amount: number;
  balance_after: number;
  transaction_type: ChefCoinTransactionType;
  reason: string | null;
  feature: string | null;
  created_at: string;
}

// Mirror of the server-side `chefcoin_costs` table (single source of truth for
// pricing). The authoritative charge is resolved server-side via
// `get_chefcoin_cost`; this map is only for instant client-side display/balance
// checks and MUST stay in sync with the table.
const costMap: Record<ChefCoinAction, number> = {
  STUDIO_GENERATE: 20,
  IMAGE_GENERATE: 15,
  RECIPE_MODIFY: 10,
  RECIPE_CREATE_FROM_TEXT: 5,
  RECIPE_CLEANUP: 5,
  RECIPE_QA: 2,
  NUTRITIONAL_ANALYSIS: 10,
  GENERAL_ADVICE: 1,
};

/** Monthly stipend + welcome bonus — keep in sync with reset_monthly_coins(). */
export const CHEFCOIN_ECONOMY = {
  WELCOME_BONUS: 100,
  STANDARD_STIPEND: 250,
  PREMIUM_STIPEND: 2000,
} as const;

export const getCostForAction = (action: ChefCoinAction): number => {
  return costMap[action] || 0;
};

export type ChefcoinResult =
  | { success: true; newBalance: number }
  | { success: false; error: string };

/**
 * Spend ChefCoins via the secure, idempotent RPC.
 * A unique idempotency key is generated per call so network retries never
 * double-charge.
 */
export const spendCoins = async (
  userId: string,
  amount: number,
  options?: { feature?: ChefCoinAction; idempotencyKey?: string },
): Promise<ChefcoinResult> => {
  if (!userId || amount <= 0) {
    return { success: false, error: 'Invalid request' };
  }

  const idempotencyKey = options?.idempotencyKey ?? crypto.randomUUID();

  const { data, error } = await supabase.rpc('spend_chefcoins', {
    p_user_id: userId,
    p_amount: amount,
    p_idempotency_key: idempotencyKey,
    p_feature: options?.feature ?? null,
  });

  if (error) {
    if (error.message.includes('INSUFFICIENT_FUNDS')) {
      return { success: false, error: 'INSUFFICIENT_FUNDS' };
    }
    if (error.message.includes('DAILY_SPEND_CAP_EXCEEDED')) {
      return { success: false, error: 'DAILY_SPEND_CAP_EXCEEDED' };
    }
    return { success: false, error: 'Failed to process transaction.' };
  }

  return { success: true, newBalance: data as number };
};

/**
 * Credit (grant) ChefCoins. Admin-only server-side; the RPC rejects non-admins.
 */
export const creditCoins = async (
  userId: string,
  amount: number,
  reason?: string,
  transactionType: 'earn' | 'admin_adjustment' = 'earn',
): Promise<ChefcoinResult> => {
  if (!userId || amount <= 0) {
    return { success: false, error: 'Invalid request' };
  }
  const { data, error } = await supabase.rpc('credit_chefcoins', {
    p_user_id: userId,
    p_amount: amount,
    p_reason: reason ?? null,
    p_transaction_type: transactionType,
    p_idempotency_key: crypto.randomUUID(),
  });
  if (error) {
    return { success: false, error: 'Failed to credit coins.' };
  }
  return { success: true, newBalance: data as number };
};

/**
 * Refund ChefCoins (admin / service-role only).
 */
export const refundCoins = async (userId: string, amount: number, reason?: string): Promise<ChefcoinResult> => {
  if (!userId || amount <= 0) {
    return { success: false, error: 'Invalid request' };
  }
  const { data, error } = await supabase.rpc('refund_chefcoins', {
    p_user_id: userId,
    p_amount: amount,
    p_reason: reason ?? null,
    p_idempotency_key: crypto.randomUUID(),
  });
  if (error) {
    return { success: false, error: 'Failed to refund coins.' };
  }
  return { success: true, newBalance: data as number };
};

/**
 * Fetch the authenticated user's ChefCoin transaction history (newest first).
 */
export const getChefcoinTransactions = async (
  userId: string,
  limit = 50,
): Promise<ChefCoinTransaction[]> => {
  const { data, error } = await supabase
    .from('chefcoin_transactions')
    .select('id, user_id, amount, balance_after, transaction_type, reason, feature, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    return [];
  }
  return (data as ChefCoinTransaction[]) || [];
};
